import { useEffect, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../state/authStore.js';
import { useI18n } from '../i18n/I18nContext.js';

/** First retry delay after a session check that could not get an answer. */
const RETRY_BASE_MS = 1_000;
/** Ceiling for the exponential backoff, so a long outage settles into a steady poll. */
const RETRY_MAX_MS = 15_000;

export function RequireAuth({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const status = useAuthStore((s) => s.status);
  const attempts = useAuthStore((s) => s.sessionAttempts);
  const checkSession = useAuthStore((s) => s.checkSession);

  useEffect(() => {
    if (status === 'checking') void checkSession();
  }, [status, checkSession]);

  /*
   * A session check that could not reach an answer heals itself. Without this,
   * `unreachable` would be a dead end — the effect above only fires on
   * `checking`, so the screen would sit there forever.
   *
   * `attempts` is in the dependency list on purpose: a retry that fails again
   * writes the same `unreachable` status, so status alone would not re-trigger
   * this effect and the retry would happen exactly once.
   */
  useEffect(() => {
    if (status !== 'unreachable') return;
    const delay = Math.min(RETRY_BASE_MS * 2 ** Math.min(attempts - 1, 4), RETRY_MAX_MS);
    const id = window.setTimeout(() => void checkSession(), delay);
    return () => window.clearTimeout(id);
  }, [status, attempts, checkSession]);

  if (status === 'checking') return <p>{t('common.loading')}</p>;

  // Never `<Navigate to="/login">` here. The session may well be valid; what
  // failed is our ability to confirm it, and bouncing to the login form throws
  // away a good session over a blip. See authStore.checkSession / OneDev #27.
  if (status === 'unreachable') {
    return (
      <div role="status">
        <p>{t('login.sessionUnreachable')}</p>
        <button type="button" className="btn btn--sm" onClick={() => void checkSession()}>
          {t('common.retry')}
        </button>
      </div>
    );
  }

  if (status === 'anonymous') return <Navigate to="/login" replace />;
  return <>{children}</>;
}
